// @flow

import * as React from "react";
import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { customValidators } from "../../utils/validators";
import { register, oktaLogin } from "../../actions/user";
import useAsyncState from "../../hooks/useAsyncState";
import BackButton from "../atoms/BackButton";
import ServerErrorMessage from "../atoms/ServerErrorMessage";
import Button from "../atoms/Button";
import CustomMeta from "../atoms/CustomMeta";
import LinkHandler from "../atoms/LinkHandler";
import Store from "../../store";

const RegisterProfileBody = (props): React.Node => {
    const options = Store.getState().options;
    const dispatch = useDispatch();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [state, localActions] = useAsyncState();
    const [manualRegister, setManualRegister] = React.useState(false);

    const handleRegisterSubmit = async (formData: any): any => {
        if (!executeRecaptcha) {
            localActions.fail("reCAPTCHA is still loading. Please try again.");
            return;
        }

        const googleRecaptchaResponse = await executeRecaptcha("register");

        localActions.request();
        props
            .register({
                ...formData,
                mobile: formData.mobile || null, // optional mobile field, pass as null if empty for BED validation
                recaptcha_token: googleRecaptchaResponse,
            })
            .then(() => {
                localActions.success();
            })
            .catch((error: any) => {
                console.error(error);

                localActions.fail(
                    error.message ||
                        "There was an issue completing registration."
                );
            });
    };

    return (
        <div className="login-profile-body container-fluid">
            <CustomMeta title="Sign up" />

            <div className="row">
                <div className="col-xs-12 col-md-offset-2 col-md-8 col-lg-10">
                    <SwitchTransition>
                        <CSSTransition
                            key={manualRegister ? 1 : 2}
                            timeout={350}
                            classNames="fade"
                            mountOnEnter
                            unmountOnExit
                        >
                            {manualRegister ? (
                                <div className="row">
                                    <div className="col-xs-12 col-md-10 col-lg-6">
                                        <BackButton
                                            className="login-profile-body__back"
                                            onClick={() =>
                                                setManualRegister(false)
                                            }
                                        >
                                            <span className="text">Back</span>
                                        </BackButton>
                                        <h3 className="login-profile-body__content__title">
                                            Sign up without UTS Email
                                        </h3>
                                        <ServerErrorMessage>
                                            {state.error}
                                        </ServerErrorMessage>
                                        <ReactForm
                                            validators={customValidators}
                                            onSuccess={handleRegisterSubmit}
                                        >
                                            <ReactFormField
                                                type="text"
                                                label="First name"
                                                name="first_name"
                                                validators={["customname"]}
                                                required={true}
                                            />
                                            <ReactFormField
                                                type="text"
                                                label="Last name"
                                                name="last_name"
                                                validators={["customname"]}
                                                required={true}
                                            />
                                            <ReactFormField
                                                type="email"
                                                label="Email"
                                                name="email"
                                                validators={["email"]}
                                                required={true}
                                            />
                                            <ReactFormField
                                                type="text"
                                                label="Mobile Number"
                                                name="mobile"
                                                validators={["phonenumber"]}
                                            />
                                            <ReactFormField
                                                type="password"
                                                label="Password"
                                                name="password"
                                                validators={["utspassword"]}
                                                required={true}
                                            />

                                            <ReactFormField
                                                className="react-form__field--remember"
                                                type="checkbox"
                                                label="I am a UTS Alumni"
                                                name="is_alumni"
                                            />

                                            <p>
                                                By signing up you confirm that
                                                you have read and accepted
                                                ActivateUTS{"\u2019"}s{" "}
                                                <LinkHandler
                                                    className="underline"
                                                    to="/terms-conditions/"
                                                    target="_blank"
                                                >
                                                    Terms & Conditions
                                                </LinkHandler>{" "}
                                                and{" "}
                                                <LinkHandler
                                                    className="underline"
                                                    to="/activate-uts-policies/privacy-policy/"
                                                    target="_blank"
                                                >
                                                    Privacy Policy
                                                </LinkHandler>
                                                . You{"\u2019"}ll receive email
                                                communications from us, and you
                                                may choose to unsubscribe at any
                                                time.
                                            </p>

                                            <Button
                                                type="submit"
                                                styles={["green"]}
                                                aria-label="Sign up"
                                                loading={state.loading}
                                                success={!!state.success}
                                            >
                                                Sign up
                                            </Button>
                                        </ReactForm>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        Sign up to access UTS discounts on
                                        events and merch or to join a club.
                                    </div>
                                    <div className="login-profile-body__cta-container">
                                        <div className="row row--padding-small">
                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    className="button button--green"
                                                    styles={[]}
                                                    onClick={() =>
                                                        dispatch(oktaLogin())
                                                    }
                                                >
                                                    Sign up with UTS email
                                                </Button>
                                            </div>
                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    styles={["green-secondary"]}
                                                    aria-label="I don’t have a UTS email"
                                                    onClick={() =>
                                                        setManualRegister(true)
                                                    }
                                                >
                                                    I don’t have a UTS email
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row row--padding-small">
                                            <div className="col-xs-12 col-sm-inline">
                                                <div
                                                    className="login-profile-body__cta-footer"
                                                    dangerouslySetInnerHTML={{
                                                        __html: options?.sign_up_disclaimer,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CSSTransition>
                    </SwitchTransition>
                </div>
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    register: register,
};

export default withRouter(
    connect(null, mapDispatchToProps)(RegisterProfileBody)
);
